import manifest from "../data/imageManifest.json";

// Canonical width set produced by scripts/optimize-images.mjs.
// Per-slug, only widths actually present on disk are listed in
// manifest[slug].widths — and that is the SOLE source of truth for
// what URLs we emit in srcsets. We do not hardcode a width here.
const CANONICAL_WIDTHS = [480, 768, 1200, 1600, 2000];

/**
 * Build a srcset string for a given slug + format. Only emits widths that
 * the manifest reports are actually on disk for that slug.
 */
function srcset(slug, ext) {
    const meta = manifest[slug];
    if (!meta) return "";
    const widths = meta.widths || CANONICAL_WIDTHS;
    return widths
        .map((w) => `/images/optimized/${slug}/${slug}-${w}.${ext} ${w}w`)
        .join(", ");
}

/**
 * Returns the URL of the largest JPEG variant we actually produced for a
 * given slug. Used for Open Graph / social cards / static preloads — i.e.
 * any context where one canonical URL is needed.
 */
export function largestJpgUrl(slug) {
    const meta = manifest[slug];
    if (!meta) return undefined;
    const widths = meta.widths || CANONICAL_WIDTHS;
    const w = widths[widths.length - 1];
    return `/images/optimized/${slug}/${slug}-${w}.jpg`;
}

/**
 * <Picture slug="hero_luxury_smart_home" alt="…" />
 *
 * Renders a <picture> with AVIF + WebP <source> elements and a JPEG
 * <img> fallback. Reads intrinsic width/height from `imageManifest.json`
 * to set width/height attributes — preventing CLS from image loads.
 */
export default function Picture({
    slug,
    alt = "",
    sizes = "100vw",
    priority = false,
    className = "",
    style,
    draggable,
    onLoad,
    ...rest
}) {
    const meta = manifest[slug];
    if (!meta) {
        // Defensive — never crash a page over a missing manifest entry.
        if (typeof console !== "undefined") {
            console.warn(`[Picture] unknown slug "${slug}"`);
        }
        return null;
    }

    const widths = meta.widths || CANONICAL_WIDTHS;
    // JPEG fallback uses the smallest sensible width that exists for the
    // slug (1200 if available, else the largest variant we have).
    const fallbackWidth = widths.includes(1200)
        ? 1200
        : widths[widths.length - 1];
    const fallback = `/images/optimized/${slug}/${slug}-${fallbackWidth}.jpg`;

    return (
        <picture>
            <source type="image/avif" srcSet={srcset(slug, "avif")} sizes={sizes} />
            <source type="image/webp" srcSet={srcset(slug, "webp")} sizes={sizes} />
            <img
                src={fallback}
                srcSet={srcset(slug, "jpg")}
                sizes={sizes}
                width={meta.width}
                height={meta.height}
                loading={priority ? "eager" : "lazy"}
                decoding="async"
                fetchPriority={priority ? "high" : "auto"}
                alt={alt}
                className={className}
                style={style}
                draggable={draggable}
                onLoad={onLoad}
                {...rest}
            />
        </picture>
    );
}

/**
 * Helper for route-aware preload: returns srcset strings keyed by format
 * for the slug. Used by <SEO lcpSlug={…} /> via react-helmet-async.
 */
export function lcpPreloadProps(slug, sizes = "100vw") {
    if (!manifest[slug]) return null;
    return {
        avifSrcSet: srcset(slug, "avif"),
        webpSrcSet: srcset(slug, "webp"),
        jpgSrcSet: srcset(slug, "jpg"),
        sizes,
    };
}
